/** @jsx jsx */
import { jsx } from '@theme-ui/core';
import { Fragment, Suspense, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Translation, translations } from 'src/shared/translation';
import { useAppConfig } from 'src/shared/contexts/app-config';
import { Watermark } from 'src/near-me-check/components/watermark';
import { Picture } from 'src/near-me-check/components/image/picture';
import GoogleConnectButton from 'src/near-me-check/components/controls/google-connect-button';
import { SpinnerFullPage } from 'src/near-me-check/components/spinner';
import { PulseIcon } from 'src/shared/components/icons';
import headerImageDesktop from 'src/assets/banners/startpage-desktop-min.jpg';
import headerImageMobile from 'src/assets/banners/startpage-mobile-min.jpg';
import { ExtraScope } from 'src/shared/contexts/extra-scope';
// eslint-disable-next-line import/no-restricted-paths
import { useSignUpData } from 'src/self-service-signup/hooks/use-sign-up-data';
import { getUserEventAttr } from 'src/shared/helpers/tracking-user-events';
import { useTheme } from 'src/theme';

import { useSearchParams } from '../../hooks/use-get-single-location-search-params';

import { ManualInputForm } from './manual-input-form';

const transl = translations.nearmecheck.start_page;

export const StartPage = () => {
    const { theme } = useTheme();
    const {
        title: customizedTitle,
        subtitle: customizedSubtitle,
        privacyPolicyUrl,
        hideUberallAttribution,
        hideGoogleConnectFlow,
        reportName,
    } = useAppConfig();
    const history = useHistory();
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(true);
    const { resetValues } = useSignUpData();
    const searchParams = useSearchParams();

    const subtitleKey = hideGoogleConnectFlow ? transl.subtitle.no_gmb : transl.subtitle.with_gmb;

    // If the user passed a custom privacy policy via data-attributes, we show it as a link
    // else we show static text
    const privacyPolicyLink =
        privacyPolicyUrl && privacyPolicyUrl.length ? (
            <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                <Translation code={transl.gmb_connect_disclaimer_text_privacy_policy} />
            </a>
        ) : (
            <Translation code={transl.gmb_connect_disclaimer_text_privacy_policy} />
        );

    function onErrorImg() {
        setIsImageLoaded(false);
    }

    useEffect(() => {
        resetValues();
    }, [resetValues]);

    useEffect(() => {
        const alreadyOnResultsPage = history.location.pathname.includes('/results');
        if (
            sessionStorage.getItem('nmcAborted') !== 'true' &&
            searchParams &&
            !alreadyOnResultsPage
        ) {
            history.replace('/results' + history.location.search);
        }
    }, [history, searchParams]);

    return (
        <Suspense fallback={<SpinnerFullPage />}>
            <ExtraScope scope="near-me-check-start-page">
                <div
                    sx={{
                        backgroundColor: 'grey.4',
                        textAlign: 'left',
                    }}
                >
                    <div
                        sx={{
                            backgroundColor: 'nearMeCheck.white',
                            display: 'flex',
                            flexDirection: ['column', 'row'],
                            alignItems: 'center',
                            paddingTop: [4, 5],
                            paddingBottom: [8, 5],
                            paddingX: [4, 11],
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            sx={{
                                display: ['block', 'flex'],
                                flexBasis: '45%',
                                justifyContent: 'center',
                                paddingRight: ['5%', '2.5%'],
                                paddingBottom: [4, 0],
                                width: ['100%', 'auto'],
                                maxWidth: ['600px', 'none'],
                            }}
                        >
                            <Picture
                                src={headerImageDesktop}
                                alt={'Man holding a laptop'}
                                sources={[{ src: headerImageMobile, maxWidth: theme.sizes.xlarge }]}
                                onError={onErrorImg}
                                sx={{
                                    display: isImageLoaded ? 'inline' : 'none',
                                    width: '100%',
                                    height: 'auto',
                                }}
                            />
                            {/* Image error fallback */}
                            <div
                                data-testid="start-page-img-error-fallback"
                                sx={{
                                    display: isImageLoaded ? 'none' : 'block',
                                    position: 'absolute',
                                    left: 0,
                                    bottom: ['auto', 0],
                                    top: [0, 'auto'],
                                    color: 'nearMeCheck.brand.sky',
                                }}
                            >
                                <PulseIcon
                                    sx={{
                                        width: ['auto', '20vw'],
                                        height: ['20vh', 'auto'],
                                        transform: ['rotate(95deg)', 'rotate(3deg)'],
                                        opacity: [0.25, 1],
                                        overflow: 'visible',
                                    }}
                                />
                            </div>
                        </div>
                        <div
                            sx={{
                                flexBasis: ['100%', '55%'],
                                maxWidth: ['600px', 'small'],
                                position: 'relative',
                                color: 'nearMeCheck.grey.rich',
                            }}
                        >
                            {!hideUberallAttribution && (
                                <Watermark sx={{ justifyContent: 'flex-start' }} />
                            )}
                            <h1
                                sx={{
                                    marginY: [3, 5],
                                    fontSize: [6, 8],
                                    color: 'nearMeCheck.grey.rich',
                                    lineHeight: 'condensed',
                                    fontFamily: 'heading',
                                    fontWeight: 'bold',
                                }}
                            >
                                {customizedTitle || <Translation code={transl.title} />}
                            </h1>
                            <p
                                sx={{
                                    marginY: [3, 5],
                                    fontSize: 2,
                                }}
                            >
                                {customizedSubtitle || (
                                    <Translation
                                        code={subtitleKey}
                                        interpolation={{ reportName }}
                                    />
                                )}
                            </p>
                            {!hideGoogleConnectFlow && (
                                <Fragment>
                                    <div sx={{ maxWidth: 'xsmall' }}>
                                        <GoogleConnectButton
                                            block
                                            {...getUserEventAttr('nmc-start-connect-google')}
                                        >
                                            <Translation code={transl.gmb_connect_button_label} />
                                        </GoogleConnectButton>
                                    </div>
                                    <p
                                        sx={{
                                            marginTop: 3,
                                            fontWeight: 'light',
                                            fontSize: 0,
                                        }}
                                    >
                                        <Translation code={transl.gmb_connect_disclaimer_text} />
                                        &nbsp;
                                        {privacyPolicyLink}
                                    </p>
                                </Fragment>
                            )}
                        </div>
                    </div>
                    <div
                        sx={{
                            paddingY: [7, 8],
                            paddingX: [4, 6],
                        }}
                    >
                        <div
                            sx={{
                                maxWidth: '600px',
                                marginX: 'auto',
                            }}
                        >
                            <ManualInputForm />
                        </div>
                    </div>
                </div>
            </ExtraScope>
        </Suspense>
    );
};

export { StartPage as default };
